import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  commitEmailAllowlist,
  commitPhoneAllowlist,
  fetchEngagement,
  previewEmailAllowlist,
  previewPhoneAllowlist,
  transitionEngagement,
  updateEngagement,
  type AgentToolName,
  type EngagementState,
  type SipTrunkChoice,
  type VideoConfig,
  type VideoConsent,
  type VoicePipelineMode,
} from "../api/engagements";

export function useEngagement(id: string) {
  return useQuery({
    queryKey: ["engagement", id],
    queryFn: () => fetchEngagement(id),
    enabled: !!id,
  });
}

export function useUpdateEngagement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      display_name?: string;
      persona_id?: string | null;
      voice_pipeline_mode?: VoicePipelineMode;
      sip_trunk_choice?: SipTrunkChoice | null;
      agent_tools_enabled?: AgentToolName[];
      max_turns_per_call?: number | null;
      recording_enabled?: boolean;
      recording_disclosure_required?: boolean;
      streaming_gating_enabled?: boolean;
      video_config?: VideoConfig;
      video_consent?: VideoConsent;
    }) => updateEngagement(id, body),
    onSuccess: (data) => {
      qc.setQueryData(["engagement", id], data);
      // Also invalidate the list so display_name change shows there
      void qc.invalidateQueries({ queryKey: ["engagements"] });
    },
  });
}

export function useTransitionEngagement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      target_state: EngagementState;
      comment?: string;
      reason?: string;
    }) => transitionEngagement(id, body),
    onSuccess: (data) => {
      qc.setQueryData(["engagement", id], data);
      void qc.invalidateQueries({ queryKey: ["engagements"] });
    },
  });
}

export function usePhoneAllowlistImport(id: string) {
  const qc = useQueryClient();
  return {
    preview: useMutation({
      mutationFn: (file: File) => previewPhoneAllowlist(id, file),
    }),
    commit: useMutation({
      mutationFn: (preview_token: string) =>
        commitPhoneAllowlist(id, preview_token),
      onSuccess: (data) => {
        qc.setQueryData(["engagement", id], data);
      },
    }),
  };
}

export function useEmailAllowlistImport(id: string) {
  const qc = useQueryClient();
  return {
    preview: useMutation({
      mutationFn: (file: File) => previewEmailAllowlist(id, file),
    }),
    commit: useMutation({
      mutationFn: (preview_token: string) =>
        commitEmailAllowlist(id, preview_token),
      onSuccess: (data) => {
        qc.setQueryData(["engagement", id], data);
      },
    }),
  };
}
